import "@/App.css";
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { registerServiceWorker } from "@/lib/pwa";

import Home from "@/pages/Home";
import Library from "@/pages/Library";
import Journal from "@/pages/Journal";
import Profile from "@/pages/Profile";
import Sessions from "@/pages/Sessions";
import StateSelector from "@/pages/StateSelector";
import MicroActionsLibrary from "@/pages/MicroActionsLibrary";
import Subscription from "@/pages/Subscription";
import { Login, Signup, ForgotPassword, ResetPassword } from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import { Terms, Privacy, RefundPolicy, MedicalDisclaimer } from "@/pages/Legal";
import BottomNav from "@/components/BottomNav";
import WelcomeOnboarding, { hasSeenWelcome } from "@/components/WelcomeOnboarding";

// Optional URL namespace. Two ways to activate, in order of precedence:
//   1. REACT_APP_BASE_PATH env var at build time (e.g. "/daily-reset")
//   2. Auto-detect: when the app is served from maayaveda.life (production),
//      we use "/daily-reset" automatically — no env var needed.
// Preview, local dev, and any other host stay at "/" with no prefix.
const PRODUCTION_HOSTS = ["maayaveda.life", "www.maayaveda.life"];
const PRODUCTION_BASE_PATH = "/daily-reset";
let BASE_PATH = (process.env.REACT_APP_BASE_PATH || "").replace(/\/$/, "");
if (!BASE_PATH && typeof window !== "undefined"
    && PRODUCTION_HOSTS.includes(window.location.hostname)) {
  BASE_PATH = PRODUCTION_BASE_PATH;
}

const LoadingShell = () => (
  <div className="app-shell flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="inline-block w-10 h-10 rounded-full bg-[#234B38]/10 animate-soft-pulse" />
      <p className="text-sm text-[#5C7568] mt-4">Loading…</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingShell />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

const AppRouter = () => {
  const location = useLocation();
  const { user, loading } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);

  // Show welcome onboarding to first-time visitors only (never authenticated).
  useEffect(() => {
    if (loading) return;
    if (!user && !hasSeenWelcome() && !location.pathname.startsWith("/reset-password")) {
      setShowWelcome(true);
    }
  }, [loading, user, location.pathname]);

  // Detect Google OAuth callback during render to prevent race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      {showWelcome && <WelcomeOnboarding onClose={() => setShowWelcome(false)} />}
      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Legal — public */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/medical-disclaimer" element={<MedicalDisclaimer />} />

        {/* Protected app */}
        <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
        <Route path="/journal" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/sessions" element={<ProtectedRoute><Sessions /></ProtectedRoute>} />
        <Route path="/state-selector" element={<ProtectedRoute><StateSelector /></ProtectedRoute>} />
        <Route path="/micro-actions" element={<ProtectedRoute><MicroActionsLibrary /></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
      </Routes>
      {user && <BottomNav />}
    </>
  );
};

function App() {
  useEffect(() => { registerServiceWorker(); }, []);

  // When a base path is configured (e.g. "/daily-reset" in production),
  // redirect any request that landed outside that namespace into it — so
  // old bookmarks/SEO links to https://maayaveda.life/login etc. flow to
  // https://maayaveda.life/daily-reset/login automatically.
  if (typeof window !== "undefined" && BASE_PATH) {
    const path = window.location.pathname;
    const isApi = path.startsWith("/api/");
    const isAsset = /^\/(static|favicon|manifest|service-worker|icon|robots|sitemap)/.test(path);
    const insideBase = path === BASE_PATH || path.startsWith(BASE_PATH + "/");
    if (!isApi && !isAsset && !insideBase) {
      const tail = path === "/" ? "/" : path;
      window.location.replace(BASE_PATH + tail + window.location.search + window.location.hash);
      return null;
    }
  }

  return (
    <div className="App">
      <BrowserRouter basename={BASE_PATH || undefined}>
        <AuthProvider>
          <AppRouter />
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                background: "#FFFFFF",
                color: "#1A2E24",
                border: "1px solid #E2EBE6",
                borderRadius: "9999px",
                fontFamily: "Work Sans, sans-serif",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
